import { useState, useRef, useCallback } from "react";

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600, display:"inline-flex", alignItems:"center", gap:6 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:6 },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  pill:    (c) => ({ fontSize:11, padding:"3px 10px", borderRadius:999, background:c+"18", border:"0.5px solid "+c+"40", color:c, display:"inline-flex", alignItems:"center", gap:5 }),
};

// ── Platform specs ──────────────────────────────────────────
const PLATFORMS = [
  {
    id:"myntra", name:"Myntra", color:"#b5003c", icon:"👗",
    w:1080, h:1440, ratio:"3:4",
    format:"JPEG", minKB:500, maxKB:1024,
    bg:"#FFFFFF", fit:"cover",
    tips:["White/light grey background only","Model or flat-lay, no mannequin","No watermarks or text overlay","Front-facing, well-lit"],
  },
  {
    id:"amazon", name:"Amazon", color:"#c45000", icon:"📦",
    w:2000, h:2000, ratio:"1:1",
    format:"JPEG", minKB:100, maxKB:10240,
    bg:"#FFFFFF", fit:"cover",
    tips:["Pure white background (RGB 255,255,255)","Product occupies 85%+ of frame","No logos, watermarks or text","No props or accessories"],
  },
  {
    id:"flipkart", name:"Flipkart", color:"#1b57c0", icon:"🛒",
    w:1000, h:1000, ratio:"1:1",
    format:"JPEG", minKB:50, maxKB:5120,
    bg:"#FFFFFF", fit:"cover",
    tips:["White background preferred","Product should fill 80%+ of frame","Multiple angles recommended","Min 500×500 px accepted"],
  },
  {
    id:"meesho", name:"Meesho", color:"#9f2089", icon:"🛍️",
    w:1080, h:1080, ratio:"1:1",
    format:"JPEG", minKB:50, maxKB:5120,
    bg:"#FFFFFF", fit:"cover",
    tips:["Square format required","Clean background preferred","Bright, clear product visibility","No text or promotional overlays"],
  },
  {
    id:"shopify", name:"Shopify", color:"#5a8a00", icon:"🏪",
    w:2048, h:2048, ratio:"1:1",
    format:"JPEG", minKB:100, maxKB:20480,
    bg:"#FFFFFF", fit:"cover",
    tips:["Consistent style across products","Square recommended for collections","Zoom-ready quality (2048px+)","Alt text for SEO"],
  },
];

function estimateBytes(dataUrl) {
  return Math.round((dataUrl.length - "data:image/jpeg;base64,".length) * 3 / 4);
}

function findOptimalQuality(canvas, minBytes, maxBytes) {
  let lo = 0.55, hi = 0.97, best = null;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    const url = canvas.toDataURL("image/jpeg", mid);
    const bytes = estimateBytes(url);
    if (bytes >= minBytes && bytes <= maxBytes) { best = url; break; }
    if (bytes < minBytes) lo = mid; else hi = mid;
  }
  return best || canvas.toDataURL("image/jpeg", 0.88);
}

function convertImage(imgEl, platform) {
  const { w, h, bg, fit, minKB, maxKB } = platform;
  const canvas = document.createElement("canvas");
  canvas.width  = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  const srcAspect = imgEl.naturalWidth / imgEl.naturalHeight;
  const tgtAspect = w / h;
  let sx, sy, sw, sh;

  if (fit === "cover") {
    // crop to fill
    if (srcAspect > tgtAspect) {
      sh = imgEl.naturalHeight; sw = sh * tgtAspect;
      sx = (imgEl.naturalWidth - sw) / 2; sy = 0;
    } else {
      sw = imgEl.naturalWidth; sh = sw / tgtAspect;
      sx = 0; sy = (imgEl.naturalHeight - sh) / 2;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, w, h);
  } else {
    // contain — letterbox with bg
    const scale = Math.min(w / imgEl.naturalWidth, h / imgEl.naturalHeight) * 0.92;
    const dw = imgEl.naturalWidth  * scale;
    const dh = imgEl.naturalHeight * scale;
    const dx = (w - dw) / 2;
    const dy = (h - dh) / 2;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imgEl, dx, dy, dw, dh);
  }

  const minBytes = minKB * 1024;
  const maxBytes = maxKB * 1024;
  return findOptimalQuality(canvas, minBytes, maxBytes);
}

// ── Single platform converter card ─────────────────────────
function ConverterCard({ platform, imgEl, origMeta }) {
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(false);

  const run = useCallback(() => {
    if (!imgEl) return;
    setLoading(true);
    setTimeout(() => {
      const dataUrl = convertImage(imgEl, platform);
      const bytes   = estimateBytes(dataUrl);
      const kb      = Math.round(bytes / 1024);
      const inRange = bytes >= platform.minKB * 1024 && bytes <= platform.maxKB * 1024;
      setResult({ dataUrl, kb, inRange });
      setDone(true);
      setLoading(false);
    }, 50);
  }, [imgEl, platform]);

  return (
    <div style={{ ...S.card, marginBottom:0 }}>
      <div style={{ padding:"10px 14px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:16 }}>{platform.icon}</span>
        <span style={{ fontWeight:700, color:platform.color, fontSize:13 }}>{platform.name}</span>
        <span style={S.pill(platform.color)}>{platform.w}×{platform.h}</span>
        <span style={S.pill("#6b7280")}>{platform.ratio}</span>
        <span style={{ marginLeft:"auto", fontSize:11, color:"#9ca3af" }}>{platform.minKB}KB–{platform.maxKB>=1024?platform.maxKB/1024+"MB":platform.maxKB+"KB"}</span>
      </div>

      <div style={{ padding:12 }}>
        {/* preview */}
        {done && result ? (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
            <div>
              <div style={{ fontSize:10, color:"#9ca3af", marginBottom:4, textTransform:"uppercase", fontWeight:600 }}>Original</div>
              <div style={{ aspectRatio: platform.w+"/"+platform.h, background:"#f3f4f6", borderRadius:6, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <img src={origMeta.src} alt="orig" style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, color:"#9ca3af", marginBottom:4, textTransform:"uppercase", fontWeight:600 }}>Converted</div>
              <div style={{ aspectRatio: platform.w+"/"+platform.h, background:"#fff", border:"1px solid #e5e7eb", borderRadius:6, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <img src={result.dataUrl} alt="converted" style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ aspectRatio: platform.w > platform.h ? "2/1" : platform.w===platform.h ? "2/1" : "2/1", background:"#f9fafb", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
            <div style={{ textAlign:"center", color:"#9ca3af", fontSize:12 }}>
              {loading ? "⏳ Converting…" : "Click Convert to process"}
            </div>
          </div>
        )}

        {/* checks */}
        {done && result && (
          <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:12 }}>
            {[
              { label:`Dimensions: ${platform.w}×${platform.h}px`, ok:true },
              { label:`Aspect ratio: ${platform.ratio}`, ok:true },
              { label:`File size: ${result.kb}KB (target ${platform.minKB}KB–${platform.maxKB>=1024?platform.maxKB/1024+"MB":platform.maxKB+"KB"})`, ok:result.inRange, warn:result.kb < platform.minKB },
              { label:"Format: JPEG", ok:true },
              { label:"Background applied", ok:true },
            ].map((c, i) => (
              <div key={i} style={{ fontSize:11, display:"flex", alignItems:"center", gap:6, color: c.ok?"#15803d":c.warn?"#b45309":"#dc2626" }}>
                <span>{c.ok?"✅":c.warn?"⚠️":"❌"}</span>{c.label}
              </div>
            ))}
          </div>
        )}

        {/* tips */}
        <div style={{ background:"#f9fafb", borderRadius:6, padding:"8px 10px", marginBottom:10 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#6b7280", marginBottom:4, textTransform:"uppercase" }}>{platform.name} image guidelines</div>
          {platform.tips.map((t, i) => (
            <div key={i} style={{ fontSize:11, color:"#374151", display:"flex", gap:5, marginBottom:2 }}>
              <span style={{ color:platform.color }}>•</span>{t}
            </div>
          ))}
        </div>

        {/* actions */}
        <div style={{ display:"flex", gap:8 }}>
          {!done
            ? <button style={S.btn(platform.color)} onClick={run} disabled={loading || !imgEl}>
                {loading ? "Converting…" : "Convert for "+platform.name}
              </button>
            : <>
                <a href={result.dataUrl} download={`product_${platform.id}.jpg`} style={{ ...S.btn(platform.color), textDecoration:"none" }}>
                  ⬇ Download
                </a>
                <button style={S.btnOut} onClick={() => { setDone(false); setResult(null); run(); }}>↻ Redo</button>
              </>
          }
        </div>
      </div>
    </div>
  );
}

// ── Convert all at once ─────────────────────────────────────
function BulkResult({ imgEl, origMeta, selectedPlatforms }) {
  const [results,  setResults]  = useState({});
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(false);

  const runAll = useCallback(() => {
    if (!imgEl) return;
    setLoading(true);
    setDone(false);
    setTimeout(() => {
      const out = {};
      selectedPlatforms.forEach((pl) => {
        const dataUrl = convertImage(imgEl, pl);
        const kb      = Math.round(estimateBytes(dataUrl) / 1024);
        out[pl.id]    = { dataUrl, kb, inRange: estimateBytes(dataUrl) >= pl.minKB*1024 && estimateBytes(dataUrl) <= pl.maxKB*1024 };
      });
      setResults(out);
      setDone(true);
      setLoading(false);
    }, 80);
  }, [imgEl, selectedPlatforms]);

  return (
    <div style={S.card}>
      <div style={S.cardHdr}>
        <span>⚡ Convert for All Platforms at Once</span>
        <button style={S.btn()} onClick={runAll} disabled={loading || !imgEl}>
          {loading ? "⏳ Converting all…" : "Convert All (" + selectedPlatforms.length + ")"}
        </button>
      </div>
      {done && (
        <div style={{ padding:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:10 }}>
            {selectedPlatforms.map((pl) => {
              const r = results[pl.id];
              if (!r) return null;
              return (
                <div key={pl.id} style={{ border:"1px solid "+pl.color+"30", borderRadius:8, overflow:"hidden" }}>
                  <div style={{ background:pl.color, color:"#fff", padding:"5px 10px", fontSize:11, fontWeight:700, display:"flex", justifyContent:"space-between" }}>
                    <span>{pl.icon} {pl.name}</span>
                    <span>{r.kb}KB</span>
                  </div>
                  <div style={{ aspectRatio:pl.w+"/"+pl.h, background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", maxHeight:140, overflow:"hidden" }}>
                    <img src={r.dataUrl} alt={pl.name} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                  </div>
                  <div style={{ padding:"6px 8px" }}>
                    <a href={r.dataUrl} download={`product_${pl.id}.jpg`} style={{ ...S.btn(pl.color), width:"100%", justifyContent:"center", textDecoration:"none", fontSize:11, padding:"5px 8px" }}>
                      ⬇ Download
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function PageImageTools({ toast }) {
  const [tab,       setTab]       = useState("converter");
  const [imgEl,     setImgEl]     = useState(null);
  const [origMeta,  setOrigMeta]  = useState(null);
  const [selPlatforms, setSelPlatforms] = useState(PLATFORMS.map((p) => p.id));
  const [activePl,  setActivePl]  = useState("myntra");
  const fileRef  = useRef();
  const dropRef  = useRef();

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) { toast("Please upload an image file", "error"); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImgEl(img);
        setOrigMeta({ src: e.target.result, w: img.naturalWidth, h: img.naturalHeight, size: file.size, name: file.name.replace(/\.[^.]+$/, ""), ratio: (img.naturalWidth / img.naturalHeight).toFixed(3) });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("drag");
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const togglePl = (id) => {
    setSelPlatforms((prev) => prev.includes(id) ? (prev.length > 1 ? prev.filter((p) => p !== id) : prev) : [...prev, id]);
  };

  const reset = () => {
    setImgEl(null);
    setOrigMeta(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const activePlatform = PLATFORMS.find((p) => p.id === activePl);
  const selectedPlatformObjs = PLATFORMS.filter((p) => selPlatforms.includes(p.id));

  return (
    <div>
      {/* header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>🖼 Image Tools</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Resize & convert product images to exact specs for Myntra, Amazon, Flipkart, Meesho & Shopify</div>
        </div>
        {imgEl && <button style={S.btnOut} onClick={reset}>↻ Upload new image</button>}
      </div>

      {/* upload zone */}
      {!imgEl ? (
        <div
          ref={dropRef}
          style={{ border:"2px dashed #e5e7eb", borderRadius:10, padding:"3rem 2rem", textAlign:"center", cursor:"pointer", background:"#fafafa", marginBottom:14, transition:"background .15s" }}
          onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("drag"); }}
          onDragLeave={() => dropRef.current?.classList.remove("drag")}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}/>
          <div style={{ fontSize:40, marginBottom:10 }}>📸</div>
          <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>Click or drag & drop your product image</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>PNG, JPG, WEBP supported · Any size or aspect ratio</div>
          <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:16, flexWrap:"wrap" }}>
            {PLATFORMS.map((pl) => (
              <span key={pl.id} style={S.pill(pl.color)}>{pl.icon} {pl.name} {pl.w}×{pl.h}</span>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* image loaded — show original info */}
          <div style={{ ...S.card, padding:14, marginBottom:14, display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
            <img src={origMeta.src} alt="uploaded" style={{ width:56, height:56, objectFit:"cover", borderRadius:8, border:"1px solid #e5e7eb" }}/>
            <div>
              <div style={{ fontWeight:600, fontSize:13 }}>{origMeta.name}</div>
              <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>
                {origMeta.w} × {origMeta.h}px · {Math.round(origMeta.size/1024)}KB · Ratio {origMeta.ratio}
              </div>
            </div>
            <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
              <span style={{ fontSize:12, color:"#15803d", fontWeight:600 }}>✅ Image loaded — ready to convert</span>
            </div>
          </div>

          {/* tabs */}
          <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
            <button style={S.tab(tab==="converter")} onClick={() => setTab("converter")}>🔄 Per Platform</button>
            <button style={S.tab(tab==="bulk")}      onClick={() => setTab("bulk")}>⚡ Convert All</button>
            <button style={S.tab(tab==="specs")}     onClick={() => setTab("specs")}>📋 Specs Guide</button>
          </div>

          {/* ── PER PLATFORM ── */}
          {tab === "converter" && (
            <>
              {/* platform selector */}
              <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
                {PLATFORMS.map((pl) => (
                  <button key={pl.id} style={{ ...S.btn(activePl===pl.id ? pl.color : "#e5e7eb"), color:activePl===pl.id?"#fff":"#374151", padding:"6px 14px", fontSize:12 }}
                    onClick={() => setActivePl(pl.id)}>
                    {pl.icon} {pl.name}
                  </button>
                ))}
              </div>
              {activePlatform && <ConverterCard key={activePlatform.id} platform={activePlatform} imgEl={imgEl} origMeta={origMeta}/>}
            </>
          )}

          {/* ── BULK ── */}
          {tab === "bulk" && (
            <>
              <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ fontSize:12, color:"#6b7280", fontWeight:600 }}>Include platforms:</span>
                {PLATFORMS.map((pl) => (
                  <button key={pl.id}
                    style={{ ...S.btn(selPlatforms.includes(pl.id) ? pl.color : "#e5e7eb"), color:selPlatforms.includes(pl.id)?"#fff":"#374151", padding:"5px 12px", fontSize:11 }}
                    onClick={() => togglePl(pl.id)}>
                    {pl.icon} {pl.name}
                  </button>
                ))}
              </div>
              <BulkResult imgEl={imgEl} origMeta={origMeta} selectedPlatforms={selectedPlatformObjs}/>
            </>
          )}

          {/* ── SPECS ── */}
          {tab === "specs" && (
            <div style={S.card}>
              <div style={S.cardHdr}><span>📋 Platform Image Specifications</span></div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:"#f9fafb" }}>
                      {["Platform","Dimensions","Ratio","Format","Min Size","Max Size","Background","Fit Mode"].map((h) => (
                        <th key={h} style={{ padding:"9px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6b7280", borderBottom:"1px solid #e5e7eb" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PLATFORMS.map((pl, i) => (
                      <tr key={pl.id} style={{ background: i%2===0?"#fff":"#fafafa" }}>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>
                          <span style={{ fontWeight:700, color:pl.color }}>{pl.icon} {pl.name}</span>
                        </td>
                        <td style={{ padding:"9px 12px", fontSize:12, fontFamily:"monospace" }}>{pl.w}×{pl.h}</td>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>{pl.ratio}</td>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>{pl.format}</td>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>{pl.minKB}KB</td>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>{pl.maxKB>=1024?pl.maxKB/1024+"MB":pl.maxKB+"KB"}</td>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>White</td>
                        <td style={{ padding:"9px 12px", fontSize:12 }}>
                          <span style={{ background: pl.fit==="cover"?"#fff0f4":"#f0f9ff", color:pl.fit==="cover"?"#b5003c":"#2563eb", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600 }}>
                            {pl.fit}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:14 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10 }}>Platform Guidelines</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10 }}>
                  {PLATFORMS.map((pl) => (
                    <div key={pl.id} style={{ border:"1px solid "+pl.color+"30", borderRadius:8, overflow:"hidden" }}>
                      <div style={{ background:pl.color, color:"#fff", padding:"6px 10px", fontSize:11, fontWeight:700 }}>{pl.icon} {pl.name}</div>
                      <div style={{ padding:"8px 10px" }}>
                        {pl.tips.map((t, i) => (
                          <div key={i} style={{ fontSize:11, color:"#374151", display:"flex", gap:5, marginBottom:3 }}>
                            <span style={{ color:pl.color, flexShrink:0 }}>•</span>{t}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
